import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { api } from '@/lib/api';
import { formatNumber, formatDateTime } from '@/lib/format';
import { exportToCsv } from '@/lib/csv';
import { Diamond, Send, Search, Download, CheckCircle2, AlertTriangle, Loader2, User, Crown } from 'lucide-react';
import { toast } from 'sonner';

export default function UserStockRecharge() {
    const [config, setConfig] = useState([]);
    const [platform, setPlatform] = useState('trumeet');
    const [inAppId, setInAppId] = useState('');
    const [diamonds, setDiamonds] = useState('');
    const [busy, setBusy] = useState(false);
    const [recharges, setRecharges] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [query, setQuery] = useState('');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [recipient, setRecipient] = useState(null); // {loading, ok, nick, avatar, reason}
    const [nobleTiers, setNobleTiers] = useState([]);
    const [nobleTier, setNobleTier] = useState('');
    const [nobleMode, setNobleMode] = useState('first');
    const [nobleBusy, setNobleBusy] = useState(false);
    const [nobleCurrent, setNobleCurrent] = useState(null);

    const loadConfig = () => api.get('/recharge/stock/config').then(({ data }) => {
        const list = data.platforms || [];
        setConfig(list);
        if (list.length && !list.find(p => p.key === platform)) setPlatform(list[0].key);
    }).catch(() => {});
    const loadRecharges = () => api.get('/recharge/stock/recharges').then(({ data }) => setRecharges(data.recharges || [])).catch(() => {});
    const loadAllocations = () => api.get('/recharge/stock/allocations').then(({ data }) => setAllocations(data.allocations || [])).catch(() => {});
    useEffect(() => { loadConfig(); loadRecharges(); loadAllocations(); }, []);
    useEffect(() => {
        api.get('/recharge/stock/noble/tiers').then(({ data }) => {
            const t = data.tiers || [];
            setNobleTiers(t);
            if (t.length && !nobleTier) setNobleTier(t[0].key);
        }).catch(() => {});
    }, []);
    // Auto-detect current nobility of the recipient (Qwick Live only).
    useEffect(() => {
        const id = inAppId.trim();
        setNobleCurrent(null);
        if (platform !== 'olaparty' || id.length < 3) return;
        let cancelled = false;
        const t = setTimeout(() => {
            api.get(`/recharge/noble/detect/${encodeURIComponent(id)}`).then(({ data }) => {
                if (cancelled || !data.ok) return;
                const cur = data.current || {};
                setNobleCurrent(cur);
                if (cur.has_noble && cur.tier_key) { setNobleTier(cur.tier_key); setNobleMode('renew'); }
            }).catch(() => {});
        }, 650);
        return () => { cancelled = true; clearTimeout(t); };
    }, [inAppId, platform]);

    const submitNoble = async () => {
        if (!inAppId.trim()) { toast.error('Enter the recipient in-app ID'); return; }
        if (!nobleTier) { toast.error('Choose a noble title'); return; }
        setNobleBusy(true);
        try {
            const { data } = await api.post('/recharge/stock/noble', { in_app_id: inAppId.trim(), tier: nobleTier, mode: nobleMode });
            if (data.ok) toast.success(`${data.tier} ${data.mode === 'renew' ? 'renewed' : 'activated'} (−${data.charge} 💎)`);
            else toast.error(data.message || 'Noble activation failed');
            loadConfig(); loadRecharges();
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not activate noble'); }
        finally { setNobleBusy(false); }
    };

    const current = useMemo(() => config.find(p => p.key === platform) || {}, [config, platform]);
    const base = parseInt(diamonds, 10) || 0;
    const mult = current.multiplier || 1;
    const inApp = base * mult;
    const balance = current.balance ?? 0;

    // Auto-fetch the recipient profile so users confirm the right ID before sending.
    useEffect(() => {
        const id = inAppId.trim();
        setRecipient(null);
        if (id.length < 3) return;
        let cancelled = false;
        setRecipient({ loading: true });
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/recharge/stock/lookup/${encodeURIComponent(id)}`, { params: { platform } });
                if (!cancelled) setRecipient(data.ok ? { ok: true, nick: data.nick, avatar: data.avatar, vid: data.vid } : { ok: false, reason: data.reason });
            } catch (e) {
                if (!cancelled) setRecipient({ ok: false, reason: e.response?.status === 404 ? 'not_found' : 'error' });
            }
        }, 550);
        return () => { cancelled = true; clearTimeout(t); };
    }, [inAppId, platform]);

    const submit = async () => {
        if (!inAppId.trim()) { toast.error('Enter the recipient in-app ID'); return; }
        if (base <= 0) { toast.error('Enter diamonds to send'); return; }
        if (base > balance) { toast.error('Insufficient stock balance'); return; }
        setBusy(true);
        try {
            const { data } = await api.post('/recharge/stock/recharge', { platform, in_app_id: inAppId.trim(), diamonds: base });
            if (data.status === 'SUCCESS') toast.success(`Recharged ${inApp.toLocaleString()} 💎 to ${inAppId.trim()}`);
            else if (data.status === 'PENDING_MANUAL') toast.message('Recharge queued — it will be completed shortly.');
            else toast.error('Recharge failed. Your stock was not deducted.');
            setInAppId(''); setDiamonds('');
            loadConfig(); loadRecharges();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not recharge');
        } finally { setBusy(false); }
    };

    const inRange = (iso) => {
        if (!iso) return true;
        const dt = new Date(iso);
        const d = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
    };
    const filtered = recharges.filter(r => inRange(r.created_at) && (!query.trim() || String(r.in_app_id || '').toLowerCase().includes(query.trim().toLowerCase())));

    const exportRecharges = () => exportToCsv('stock-recharges', filtered, [
        { header: 'Time', accessor: r => formatDateTime(r.created_at) },
        { header: 'Platform', accessor: r => r.platform },
        { header: 'In-app ID', accessor: r => r.in_app_id },
        { header: 'Diamonds', accessor: r => r.amount },
        { header: 'Status', accessor: r => r.status },
        { header: 'Balance after', accessor: r => r.balance_after },
    ]);
    const exportAllocations = () => exportToCsv('stock-allocations', allocations, [
        { header: 'Time', accessor: a => formatDateTime(a.created_at) },
        { header: 'Platform', accessor: a => a.platform },
        { header: 'Diamonds', accessor: a => a.amount },
        { header: 'Source', accessor: a => a.source },
        { header: 'Note', accessor: a => a.note },
    ]);

    return (
        <div className="space-y-4" data-testid="user-stock-recharge">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="p-5 lg:col-span-2 space-y-4">
                    <div className="font-display font-semibold">Recharge from your stock</div>
                    <div>
                        <Label className="text-xs">Platform</Label>
                        <div className="flex flex-wrap gap-2 mt-1" data-testid="user-stock-platform-selector">
                            {config.map(p => (
                                <button key={p.key} type="button" onClick={() => setPlatform(p.key)}
                                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${platform === p.key ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-foreground border-border hover:border-foreground/30'}`}
                                    data-testid={`user-stock-platform-${p.key}`}>{p.name}</button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs">Recipient in-app ID</Label>
                            <Input value={inAppId} onChange={e => setInAppId(e.target.value)} placeholder="e.g. 123456789" data-testid="user-stock-recharge-inappid" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Diamonds to send (from balance)</Label>
                            <Input type="number" min="1" value={diamonds} onChange={e => setDiamonds(e.target.value)} placeholder="e.g. 100" data-testid="user-stock-recharge-diamonds" />
                        </div>
                    </div>
                    {recipient && (
                        <div className="rounded-lg border px-3 py-2 flex items-center gap-3 text-sm"
                            data-testid="user-stock-recipient-card"
                            style={{ borderColor: recipient.ok ? '#bbf7d0' : '#fde68a', background: recipient.ok ? '#f0fdf4' : '#fffbeb' }}>
                            {recipient.loading ? (
                                <><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /><span className="text-muted-foreground">Looking up recipient…</span></>
                            ) : recipient.ok ? (
                                <>
                                    {recipient.avatar ? <img src={recipient.avatar} alt="" className="h-9 w-9 rounded-full object-cover border" onError={e => { e.target.style.display = 'none'; }} />
                                        : <span className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center"><User className="h-4 w-4 text-emerald-700" /></span>}
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-1.5 font-medium text-emerald-900" data-testid="user-stock-recipient-nick"><CheckCircle2 className="h-4 w-4" /> {recipient.nick || 'Recipient found'}</div>
                                        <div className="text-xs text-muted-foreground">ID {recipient.vid || inAppId.trim()} — please confirm this is correct</div>
                                    </div>
                                </>
                            ) : (
                                <><AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                    <span className="text-amber-800">{recipient.reason === 'not_found' ? "Couldn't find this ID on the platform — double-check before sending." : "Couldn't verify this ID right now. Make sure it's correct before sending."}</span></>
                            )}
                        </div>
                    )}

                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center justify-between text-sm" data-testid="user-stock-recharge-preview">
                        <span className="text-emerald-900">Recipient receives</span>
                        <span className="font-display font-semibold text-emerald-900">{inApp.toLocaleString()} 💎{mult > 1 ? ` (${base.toLocaleString()} × ${mult})` : ''}</span>
                    </div>
                    <Button onClick={submit} disabled={busy || base <= 0} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="user-stock-recharge-submit">
                        <Send className="h-4 w-4 mr-2" /> {busy ? 'Sending…' : 'Recharge now'}
                    </Button>
                </Card>
                <Card className="p-5 bg-[#0E2A47] text-white">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-3">{current.name || 'Current'} balance</div>
                    <div className="flex items-baseline gap-2">
                        <Diamond className="w-6 h-6 text-white/60" />
                        <div className="font-display text-4xl font-semibold tabular-nums" data-testid="user-stock-recharge-balance">{formatNumber(balance)}</div>
                    </div>
                    <p className="mt-4 text-xs text-white/60">Top up your stock from the Stock Wallet tab. Diamonds are credited after admin approves your bank transfer.</p>
                </Card>
            </div>

            {platform === 'olaparty' && nobleTiers.length > 0 && (
                <Card className="p-4 sm:p-5 border-amber-200" data-testid="user-stock-noble-card">
                    <div className="flex items-center gap-2 font-display font-semibold"><Crown className="h-4 w-4 text-amber-600" /> Activate Noble from stock (Qwick Live)</div>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-3">Uses your Qwick Live stock balance. Enter the recipient ID above first.</p>
                    {nobleCurrent && nobleCurrent.has_noble && (
                        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-sm text-amber-900" data-testid="user-stock-noble-current">
                            Recipient holds <strong>{nobleCurrent.tier_label || 'a noble title'}</strong>{nobleCurrent.expire_at ? ` (expires ${new Date(nobleCurrent.expire_at * 1000).toLocaleDateString()})` : ''} — Renewal pre-selected.
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="user-stock-noble-tiers">
                        {nobleTiers.map(t => (
                            <button key={t.key} type="button" onClick={() => setNobleTier(t.key)}
                                className={`px-3 py-2 rounded-lg border text-sm font-medium ${nobleTier === t.key ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-foreground border-border hover:border-amber-400'}`}
                                data-testid={`user-stock-noble-tier-${t.key}`}>{t.label}</button>
                        ))}
                    </div>
                    <div className="flex gap-2 mb-3">
                        {['first', 'renew'].map(m => (
                            <button key={m} type="button" onClick={() => setNobleMode(m)}
                                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium ${nobleMode === m ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white border-border'}`}
                                data-testid={`user-stock-noble-mode-${m}`}>{m === 'first' ? 'First-time' : 'Renewal'}</button>
                        ))}
                    </div>
                    {(() => {
                        const t = nobleTiers.find(x => x.key === nobleTier);
                        const cost = t ? (nobleMode === 'renew' ? t.renew_diamonds : t.first_diamonds) : 0;
                        return (
                            <div className="flex items-center justify-between rounded-lg bg-secondary/50 px-3 py-2 text-sm mb-3" data-testid="user-stock-noble-cost">
                                <span>Cost from stock</span><span className="font-display font-semibold">{cost ? `${cost.toLocaleString()} 💎` : '—'}</span>
                            </div>
                        );
                    })()}
                    <Button onClick={submitNoble} disabled={nobleBusy || !inAppId.trim()} className="bg-amber-500 hover:bg-amber-600" data-testid="user-stock-noble-submit">
                        <Crown className="h-4 w-4 mr-2" /> {nobleBusy ? 'Activating…' : 'Activate noble'}
                    </Button>
                </Card>
            )}

            <Card className="p-4 sm:p-5">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div className="font-display font-semibold">Stock recharge history</div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Input type="date" value={start} max={end || undefined} onChange={e => setStart(e.target.value)} className="h-9 w-36" data-testid="user-stock-recharge-start" />
                            <span>to</span>
                            <Input type="date" value={end} min={start || undefined} onChange={e => setEnd(e.target.value)} className="h-9 w-36" data-testid="user-stock-recharge-end" />
                            {(start || end) && <Button variant="ghost" size="sm" onClick={() => { setStart(''); setEnd(''); }}>Clear</Button>}
                        </div>
                        <div className="relative w-full sm:w-56">
                            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search in-app ID…" className="pl-8" data-testid="user-stock-recharge-search" />
                            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        </div>
                        <Button variant="outline" size="sm" onClick={exportRecharges} data-testid="user-stock-recharge-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
                    </div>
                </div>
                <div className="overflow-x-auto" data-testid="user-stock-recharge-history">
                    <Table>
                        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Platform</TableHead><TableHead>In-app ID</TableHead><TableHead className="text-right">Diamonds</TableHead><TableHead className="text-right">Balance after</TableHead><TableHead className="text-right">Status</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {filtered.map(r => (
                                <TableRow key={r.id}>
                                    <TableCell className="whitespace-nowrap">{formatDateTime(r.created_at)}</TableCell>
                                    <TableCell className="capitalize">{r.platform}</TableCell>
                                    <TableCell className="font-mono text-xs">{r.in_app_id}</TableCell>
                                    <TableCell className="text-right tabular-nums">{formatNumber(r.amount)}</TableCell>
                                    <TableCell className="text-right tabular-nums text-muted-foreground">{r.balance_after != null ? formatNumber(r.balance_after) : '—'}</TableCell>
                                    <TableCell className="text-right"><StatusBadge status={r.status} /></TableCell>
                                </TableRow>
                            ))}
                            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{query.trim() ? 'No recharges match that in-app ID.' : 'No stock recharges yet.'}</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            <Card className="p-4 sm:p-5">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div className="font-display font-semibold">Stock allocation history</div>
                    <Button variant="outline" size="sm" onClick={exportAllocations} data-testid="user-stock-alloc-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
                </div>
                <div className="overflow-x-auto" data-testid="user-stock-allocation-history">
                    <Table>
                        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Platform</TableHead><TableHead className="text-right">Diamonds</TableHead><TableHead>Source</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {allocations.map(a => (
                                <TableRow key={a.id}>
                                    <TableCell className="whitespace-nowrap">{formatDateTime(a.created_at)}</TableCell>
                                    <TableCell className="capitalize">{a.platform}</TableCell>
                                    <TableCell className="text-right tabular-nums text-emerald-700">+{formatNumber(a.amount)}</TableCell>
                                    <TableCell><span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-secondary">{a.source === 'stock_order' ? 'Bank transfer' : 'Admin credit'}</span></TableCell>
                                    <TableCell className="text-muted-foreground text-xs">{a.note}</TableCell>
                                </TableRow>
                            ))}
                            {allocations.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No allocations yet.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    );
}
